// pages/Provincias.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Statistic,
  Divider,
  Card,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  EnvironmentOutlined,  
  ClusterOutlined,
  FlagOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const Provincias = () => {
  // Estados
  const [provincias, setProvincias] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [comunidades, setComunidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentProvincia, setCurrentProvincia] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchProvincias();
    fetchComunidades();
  }, []);

  const fetchProvincias = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/provincias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProvincias(response.data.data);
      setFilteredData(response.data.data);
      
      console.log('✅ Provincias cargadas:', response.data.data);
    } catch (error) {
      message.error('Error al cargar las provincias');
      console.error('Error fetchProvincias:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchComunidades = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/provincias/comunidades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComunidades(response.data.data);
    } catch (error) {
      console.error('Error cargando comunidades:', error);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      // Si no hay texto, mostrar todos los registros
      setFilteredData(provincias);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    // Buscar en todos los campos de cada registro
    const filtered = provincias.filter(record => {
      return Object.keys(record).some(key => {
        const value = record[key];
        
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Mapear código de comunidad a nombre completo
  const getNombreComunidad = (codigo) => {
    const comunidad = comunidades.find(c => c.codigo === codigo);
    return comunidad ? comunidad.nombre : codigo;
  };

  // Configuración de columnas
  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
      sorter: (a, b) => a.codigo.localeCompare(b.codigo),
      sortOrder: sortedInfo.columnKey === 'codigo' && sortedInfo.order,
      fixed: 'left',
    },
    {
      title: 'Provincia',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
      sorter: (a, b) => a.descripcion.localeCompare(b.descripcion),
      sortOrder: sortedInfo.columnKey === 'descripcion' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'Comunidad',
      dataIndex: 'comunidad',
      key: 'comunidad',
      width: 150,
      sorter: (a, b) => (a.comunidad || '').localeCompare(b.comunidad || ''),
      sortOrder: sortedInfo.columnKey === 'comunidad' && sortedInfo.order,
      filters: comunidades.map(comunidad => ({
        text: `${comunidad.codigo} - ${comunidad.nombre}`,
        value: comunidad.codigo
      })),
      filteredValue: filteredInfo.comunidad || null,
      onFilter: (value, record) => record.comunidad === value,
      render: (comunidad) => {
        const nombreComunidad = getNombreComunidad(comunidad);
        let color = 'blue';
        
        // Asignar colores según la comunidad
        if (comunidad === 'AND') color = 'green';
        if (comunidad === 'CAT') color = 'orange';
        if (comunidad === 'MAD') color = 'red';
        if (comunidad === 'GAL') color = 'cyan';
        if (comunidad === 'PSV') color = 'purple';
        if (comunidad === 'EXT') color = 'gold';
        
        return (
          <Tag color={color} style={{ fontWeight: 'bold' }}>
            {comunidad} - {nombreComunidad}
          </Tag>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar provincia?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentProvincia(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentProvincia(record);
    
    console.log("📝 Registro para editar:", record);
    
    form.setFieldsValue({
      ...record,
      codigo: record.codigo.toString().padStart(4, '0')
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Provincia: ${record.descripcion}`,
      width: 700,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <strong>Código:</strong>
            </Col>
            <Col span={18}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                {record.codigo}
              </Tag>
            </Col>
            
            <Col span={6}>
              <strong>Provincia:</strong>
            </Col>
            <Col span={18}>
              <Text strong style={{ fontSize: '16px' }}>{record.descripcion}</Text>
            </Col>
            
            <Col span={6}>
              <strong>Comunidad:</strong>
            </Col>
            <Col span={18}>
              <Badge 
                status="processing" 
                text={
                  <span style={{ fontWeight: 'bold' }}>
                    {record.comunidad} - {getNombreComunidad(record.comunidad)}
                  </span>
                } 
              />
            </Col>
            
            <Col span={24}>
              <Divider orientation="left">Información Geográfica</Divider>
            </Col>
            
            <Col span={24}>
              <Card size="small">
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <Statistic 
                      title="Código INE" 
                      value={record.codigo} 
                      prefix={<FlagOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="Comunidad" 
                      value={record.comunidad}
                      prefix={<ClusterOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic 
                      title="Tipo" 
                      value="Provincia"
                      prefix={<EnvironmentOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/provincias/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remover del estado local
      const nuevasProvincias = provincias.filter(p => p.codigo !== codigo);
      setProvincias(nuevasProvincias);
      
      // También actualizar filteredData
      const nuevoFilteredData = filteredData.filter(p => p.codigo !== codigo);
      setFilteredData(nuevoFilteredData);
      
      message.success('Provincia eliminada correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar la provincia');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      console.log('📝 Datos a guardar:', values);
      
      // Asegurar que el código tenga 4 dígitos
      if (values.codigo) {
        values.codigo = values.codigo.toString().padStart(4, '0');
      }
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/provincias`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Agregar al estado local
        setProvincias([...provincias, response.data.data]);
        
        // Si hay búsqueda activa, actualizar filteredData también
        if (globalSearchText) {
          const lowerSearchText = globalSearchText.toLowerCase();
          const shouldShowInSearch = Object.keys(response.data.data).some(key => {
            const value = response.data.data[key];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
          
          if (shouldShowInSearch) {
            setFilteredData([...filteredData, response.data.data]);
          }
        } else {
          setFilteredData([...filteredData, response.data.data]);
        }
        
        message.success('Provincia creada correctamente');
        
      } else {
        // Edición
        console.log("📝 Editando provincia:", currentProvincia.codigo);
        console.log("📝 Valores:", values);
        
        const response = await axios.put(
          `${BASE_URL}/api/provincias/${currentProvincia.codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local
        const nuevasProvincias = provincias.map(p => 
          p.codigo === currentProvincia.codigo ? response.data.data : p
        );
        
        setProvincias(nuevasProvincias);
        
        // Actualizar filteredData
        const nuevoFilteredData = filteredData.map(p => 
          p.codigo === currentProvincia.codigo ? response.data.data : p
        );
        
        setFilteredData(nuevoFilteredData);
        
        message.success('Provincia actualizada correctamente');
      }
      
      // Cerrar modal y limpiar formulario
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error en handleSave:', error);
      console.error('❌ Response:', error.response);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.data?.details) {
        message.error(error.response.data.details);
      } else {
        message.error('Error al guardar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    console.log('Filtros aplicados:', filters);
    
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    // Aplicar filtros de columnas
    let result = [...provincias];
    
    // Aplicar filtros de Ant Design
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'comunidad') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    // Aplicar ordenación
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        }
        return 0;
      });
    }
    
    // Aplicar búsqueda global si existe
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(provincias);
    message.success('Filtros y búsqueda limpiados');
  };

  // Función para exportar
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas por comunidad
  const getEstadisticasPorComunidad = () => {
    const conteo = {};
    provincias.forEach(p => {
      const nombreComunidad = getNombreComunidad(p.comunidad);
      if (!conteo[nombreComunidad]) {
        conteo[nombreComunidad] = 0;
      }
      conteo[nombreComunidad]++;
    });
    
    return Object.entries(conteo).sort((a, b) => b[1] - a[1]);
  };

  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Provincias" 
          value={provincias.length}
          prefix={<EnvironmentOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Comunidades" 
          value={new Set(provincias.map(p => p.comunidad)).size}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Andalucía" 
          value={provincias.filter(p => p.comunidad === 'AND').length}
          valueStyle={{ color: '#faad14' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Cataluña" 
          value={provincias.filter(p => p.comunidad === 'CAT').length}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por código, provincia, comunidad, etc...."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            // Si el usuario borra todo el texto, buscar inmediatamente
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> provincias
        </Tag>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Provincias"
        subtitle={`${provincias.length} provincias registradas`}
        icon={<ClusterOutlined />}
        loading={tableLoading}
        onRefresh={fetchProvincias}
        onCreate={handleCreate}
        onExport={handleExport}
        badgeCount={provincias.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{provincias.length}</strong> provincias
            </Tag>
            <Tag color="green" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{new Set(provincias.map(p => p.comunidad)).size}</strong> comunidades
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}

        {/* Resumen por comunidades */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card 
              title="Distribución por Comunidades" 
              size="small"
              extra={<ClusterOutlined />}
            >
              <Row gutter={[8, 8]}>
                {getEstadisticasPorComunidad().slice(0, 8).map(([comunidad, count]) => (
                  <Col key={comunidad} span={6}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Text strong>{comunidad}</Text>
                      <br />
                      <Text type="secondary">{count} provincias</Text>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>

        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="codigo"
          loading={tableLoading}
          onChange={handleChange}
          scroll={{ x: 800 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nueva Provincia' : 'Editar Provincia'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código"
                rules={[
                  { required: true, message: 'Por favor ingrese el código' },
                  { pattern: /^\d{4}$/, message: 'El código debe ser un número de 4 dígitos' }
                ]}
                tooltip="Código INE de 4 dígitos (ej: 0028 para Madrid)"
              >
                <Input 
                  placeholder="0001" 
                  disabled={modalType === 'edit'} 
                  maxLength={4}
                  showCount 
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="descripcion"
                label="Nombre de la Provincia"
                rules={[{ required: true, message: 'Por favor ingrese el nombre de la provincia' }]}
              >
                <Input placeholder="Ej: Madrid" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="comunidad"
                label="Comunidad Autónoma"
                rules={[{ required: true, message: 'Por favor seleccione la comunidad' }]}
              >
                <Select
                  showSearch
                  placeholder="Seleccione comunidad autónoma"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {comunidades.map(comunidad => (
                    <Option key={comunidad.codigo} value={comunidad.codigo}>
                      {comunidad.codigo} - {comunidad.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Información adicional</Divider>
          
          <Row gutter={16}>
            <Col span={24}>
              <Card size="small" title="Notas">
                <Text type="secondary">
                  • El código debe ser de 4 dígitos según el INE<br/>
                  • La comunidad debe ser código de 3 letras<br/>
                  • No se pueden modificar los códigos existentes<br/>
                  • Las provincias están ordenadas por código INE
                </Text>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default Provincias;